import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { useLanguage } from '../context/LanguageContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { districtsApi, propertiesApi } from '../services/api';
import { X, ChevronRight, MapPin, Bed, Maximize } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = 'AIzaSyDDLFJV5Xq5U2xPoUtf5lAO6RfeiJSocJA';

const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

const tashkentCenter = {
  lat: 41.2995,
  lng: 69.2401
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#c9d6e3' }]
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [{ color: '#f5f5f5' }]
    }
  ]
};

const MapPage = () => {
  const { t, getLocalizedText } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [districts, setDistricts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(searchParams.get('district') || null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [zoom, setZoom] = useState(11);
  const [center, setCenter] = useState(tashkentCenter);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [districtsData, propertiesData] = await Promise.all([
          districtsApi.getAll(),
          propertiesApi.getAll()
        ]);
        setDistricts(districtsData);
        setProperties(propertiesData);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const filteredProperties = selectedDistrict
    ? properties.filter(p => p.district_id === selectedDistrict || p.district === selectedDistrict)
    : properties;

  const handleDistrictClick = useCallback((districtId) => {
  // === DEBUG LOGS ===
  console.log('🚨 DISTRICT CLICKED:', districtId);
  console.log('📊 All districts:', districts);
  console.log('🔍 Searching for district_id ===', districtId);
  // === END DEBUG ===
  
  const district = districts.find(d => d.district_id === districtId);
  
  console.log('✅ Found district:', district);
  
  if (district) {
    setSelectedDistrict(districtId);
    setSelectedProperty(null);
    setCenter({ lat: district.lat, lng: district.lng });
    setZoom(13);
    setSearchParams({ district: districtId });
    console.log('🎯 District selected, map should zoom');
  } else {
    console.error('❌ District NOT FOUND!');
    console.error('Available district_ids:', districts.map(d => d.district_id));
  }
}, [districts, setSearchParams]);

  const handlePropertyClick = useCallback((property) => {
    setSelectedProperty(property);
    setCenter({ lat: property.lat, lng: property.lng });
    setZoom(15);
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedDistrict(null);
    setSelectedProperty(null);
    setCenter(tashkentCenter);
    setZoom(11);
    setSearchParams({});
  }, [setSearchParams]);

  const onMapLoad = useCallback(() => {
    setIsMapLoaded(true);
  }, []);

  const getPropertyIcon = (type) => {
    const baseUrl = 'https://maps.google.com/mapfiles/ms/icons/';
    switch (type) {
      case 'apartment': return baseUrl + 'blue-dot.png';
      case 'house': return baseUrl + 'green-dot.png';
      case 'commercial': return baseUrl + 'orange-dot.png';
      default: return baseUrl + 'red-dot.png';
    }
  };

  const formatPrice = (price, priceType) => {
    // If price is already a string with currency symbol, return as-is
    if (typeof price === 'string' && (price.includes('$') || price.includes('€') || price.includes('UZS') || price.includes('₩'))) {
      return priceType === 'rent' ? `${price}/mo` : price;
    }
    
    // Otherwise format as number
    const formatted = Number(price).toLocaleString();
    return priceType === 'rent' ? `$${formatted}/mo` : `$${formatted}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="pt-16 h-screen flex">
        {/* Sidebar */}
        <div className="w-full md:w-96 bg-white border-r border-gray-200 flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {selectedDistrict 
                  ? getLocalizedText(districts.find(d => d.district_id === selectedDistrict)?.name)
                  : 'Tashkent Districts'
                }
              </h2>
              {selectedDistrict && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            {selectedDistrict && (
              <p className="text-sm text-gray-500">
                {filteredProperties.length} {t('properties')}
              </p>
            )}
          </div>

          {/* Content */}
          <ScrollArea className="flex-1">
            {!selectedDistrict ? (
              // District List
              <div className="p-4 space-y-2">
                {districts.map((district) => (
                  <button
                    key={district.district_id}
                    onClick={() => handleDistrictClick(district.district_id)}
                    className="w-full p-4 bg-gray-50 hover:bg-blue-50 rounded-xl flex items-center justify-between transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center group-hover:bg-blue-600 transition-colors">
                        <MapPin className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium text-gray-900">{getLocalizedText(district.name)}</p>
                        <p className="text-sm text-gray-500">{district.propertyCount} {t('properties')}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
                  </button>
                ))}
              </div>
            ) : (
              // Property List
              <div className="p-4 space-y-4">
                {filteredProperties.length > 0 ? (
                  filteredProperties.map((property) => (
                    <button
                      key={property.property_id || property.id}
                      onClick={() => handlePropertyClick(property)}
                      className={`w-full text-left rounded-xl overflow-hidden border-2 transition-all ${
                        selectedProperty?.property_id === (property.property_id || property.id)
                          ? 'border-blue-500 shadow-lg'
                          : 'border-transparent hover:border-gray-200'
                      }`}
                    >
                      <div className="flex gap-3 p-3 bg-white">
                        <img
                          src={property.images[0]}
                          alt={getLocalizedText(property.title)}
                          className="w-24 h-20 object-cover rounded-lg"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {getLocalizedText(property.title)}
                          </p>
                          <p className="text-blue-600 font-bold mt-1">
                            {formatPrice(property.price, property.priceType)}
                          </p>
                          <div className="flex items-center gap-3 mt-2 text-gray-500 text-xs">
                            {property.bedrooms > 0 && (
                              <span className="flex items-center gap-1">
                                <Bed className="w-3 h-3" /> {property.bedrooms}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Maximize className="w-3 h-3" /> {property.area} {t('sqm')}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    No properties found in this district
                  </div>
                )}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Map */}
        <div className="flex-1 relative hidden md:block">
          <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={zoom}
              onLoad={onMapLoad}
              options={mapOptions}
            >
              {/* District Markers - Simple URL icons */}
              {isMapLoaded && !selectedDistrict && districts.map((district) => (
                <Marker
                  key={district.district_id}
                  position={{ lat: district.lat, lng: district.lng }}
                  onClick={() => handleDistrictClick(district.district_id)}
                  icon={{
                    url: 'https://maps.google.com/mapfiles/ms/icons/blue-pushpin.png',
                  }}
                  title={getLocalizedText(district.name)}
                />
              ))}

              {/* Property Markers */}
              {isMapLoaded && selectedDistrict && filteredProperties.map((property) => (
                <Marker
                  key={property.id}
                  position={{ lat: property.lat, lng: property.lng }}
                  onClick={() => handlePropertyClick(property)}
                  icon={{
                    url: getPropertyIcon(property.type),
                  }}
                />
              ))}

              {/* Info Window */}
              {selectedProperty && (
                <InfoWindow
                  position={{ lat: selectedProperty.lat, lng: selectedProperty.lng }}
                  onCloseClick={() => setSelectedProperty(null)}
                >
                  <div className="p-2 max-w-xs">
                    <img
                      src={selectedProperty.images[0]}
                      alt={getLocalizedText(selectedProperty.title)}
                      className="w-full h-32 object-cover rounded-lg mb-3"
                    />
                    <h3 className="font-semibold text-gray-900 mb-1">
                      {getLocalizedText(selectedProperty.title)}
                    </h3>
                    <p className="text-blue-600 font-bold mb-2">
                      {formatPrice(selectedProperty.price, selectedProperty.priceType)}
                    </p>
                    <div className="flex items-center gap-3 text-gray-500 text-sm mb-3">
                      {selectedProperty.bedrooms > 0 && (
                        <span>{selectedProperty.bedrooms} {t('bedrooms')}</span>
                      )}
                      <span>{selectedProperty.area} {t('sqm')}</span>
                    </div>
                    <Link to={`/property/${selectedProperty.property_id || selectedProperty.id}`}>
                      <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700">
                        {t('viewDetails')}
                      </Button>
                    </Link>
                  </div>
                </InfoWindow>
              )}
            </GoogleMap>
          </LoadScript>

          {/* Legend */}
          <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-lg p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Property Types</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-3 h-3 bg-blue-500 rounded-full" />
                {t('apartments')}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                {t('houses')}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <div className="w-3 h-3 bg-orange-500 rounded-full" />
                {t('commercial')}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
